import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Shield, UserPlus, Trash2, Mail, Users, Crown } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import apiClient from '../utils/api';
import { useAuthStore } from '../store/authStore';
import { toast } from 'sonner';
import AdminLayout from '../components/AdminLayout';

export default function AdminManagement() {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newAdminEmail, setNewAdminEmail] = useState('');
  const [adding, setAdding] = useState(false);

  useEffect(() => {
    if (!user) return;
    if (user.role !== 'admin' && user.role !== 'owner' && user.role !== 'overseer') {
      navigate('/dashboard');
      return;
    }
    fetchAdmins();
  }, [user, navigate]);

  const fetchAdmins = async () => {
    try {
      const response = await apiClient.get('/admin/users');
      setAdmins(response.data);
    } catch (error) {
      toast.error('Failed to load admins');
    } finally {
      setLoading(false);
    }
  };

  const handleAddAdmin = async (e) => {
    e.preventDefault();
    if (!newAdminEmail.trim()) {
      toast.error('Please enter an email address');
      return;
    }

    setAdding(true);
    try {
      await apiClient.post(`/admin/users/add?email=${encodeURIComponent(newAdminEmail)}`);
      toast.success(`Successfully added ${newAdminEmail} as admin!`);
      setNewAdminEmail('');
      fetchAdmins();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add admin');
    } finally {
      setAdding(false);
    }
  };

  const handleRemoveAdmin = async (adminUser) => {
    if (adminUser.user_id === user?.user_id) {
      toast.error("You can't remove yourself as admin");
      return;
    }

    if (!window.confirm(`Are you sure you want to remove ${adminUser.email} as admin?`)) {
      return;
    }

    try {
      await apiClient.delete(`/admin/users/${adminUser.user_id}`);
      toast.success(`Removed ${adminUser.email} from admins`);
      fetchAdmins();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to remove admin');
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="min-h-[60vh] flex items-center justify-center">
          <div className="w-16 h-16 border-4 border-violet-500 border-t-transparent rounded-full animate-spin" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex items-center gap-3 mb-8">
          <div className="w-12 h-12 rounded-full bg-violet-500/20 flex items-center justify-center">
            <Shield className="text-violet-400" size={24} />
          </div>
          <div>
            <h1 className="text-2xl font-bold">Admin Management</h1>
            <p className="text-white/60">Manage administrator access</p>
          </div>
        </div>

        {/* Add New Admin */}
        <Card className="glass-default border-white/10 mb-8">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <UserPlus className="text-green-400" size={20} />
              Add New Admin
            </CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleAddAdmin} className="flex gap-4">
              <div className="flex-1">
                <Input
                  type="email"
                  placeholder="Enter user's email address"
                  value={newAdminEmail}
                  onChange={(e) => setNewAdminEmail(e.target.value)}
                  className="bg-white/5 border-white/10 focus:border-violet-500 text-white"
                />
                <p className="text-xs text-white/40 mt-2">
                  Note: The user must have already signed up before they can be made an admin
                </p>
              </div>
              <Button
                type="submit"
                disabled={adding}
                className="bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-500 hover:to-emerald-500"
              >
                {adding ? 'Adding...' : 'Add Admin'}
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* Current Admins */}
        <Card className="glass-default border-white/10">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Users className="text-violet-400" size={20} />
              Current Admins ({admins.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {admins.map((admin) => (
                <div
                  key={admin.user_id}
                  className="flex items-center justify-between p-4 bg-white/5 rounded-xl hover:bg-white/10 transition"
                >
                  <div className="flex items-center gap-4">
                    {admin.picture ? (
                      <img
                        src={admin.picture}
                        alt={admin.name}
                        className="w-12 h-12 rounded-full border-2 border-violet-500"
                      />
                    ) : (
                      <div className="w-12 h-12 rounded-full bg-violet-500/20 flex items-center justify-center">
                        <Crown className="text-violet-400" size={20} />
                      </div>
                    )}
                    <div>
                      <p className="font-semibold flex items-center gap-2">
                        {admin.name}
                        {admin.user_id === user?.user_id && (
                          <span className="text-xs bg-violet-500/30 text-violet-300 px-2 py-0.5 rounded">
                            You
                          </span>
                        )}
                      </p>
                      <p className="text-sm text-white/60 flex items-center gap-1">
                        <Mail size={14} />
                        {admin.email}
                      </p>
                    </div>
                  </div>
                  
                  {admin.user_id !== user?.user_id && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleRemoveAdmin(admin)}
                      className="text-red-400 hover:text-red-300 hover:bg-red-500/20"
                    >
                      <Trash2 size={18} />
                    </Button>
                  )}
                </div>
              ))}

              {admins.length === 0 && (
                <div className="text-center py-8 text-white/40">
                  No admins found
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  );
}
